import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useAddToCart,
  useCurrentPlans,
  useShoppingList,
  useStockPantry,
} from '../api/planner';
import type { ListItem } from '../api/planner';
import { useCorrectMatch } from '../api/review';

/**
 * A look at the basket before anything is sent to the store.
 *
 * The cart hand-off used to fire straight off the shopping list, which left
 * nowhere to say "that is not the right product" or "I already have that" —
 * and a wrong product match is silent, because a jar of pumpkin butter costs
 * about what a pack of butter costs.
 *
 * Both corrections here are permanent on purpose. Rejecting a product records
 * a human verdict that outranks the matcher at every future refresh, and
 * having something already puts it in the pantry, so next week's list knows
 * too. A one-off exclusion would need making again every week.
 */
export default function CartReview() {
  const { data: plansPage } = useCurrentPlans();
  const plans = plansPage?.results ?? [];
  const activePlan = plans.find((plan) => plan.status === 'accepted') ?? plans[0];

  const { data: list, isLoading } = useShoppingList(activePlan?.id);
  const addToCart = useAddToCart();
  const correctMatch = useCorrectMatch();
  const stock = useStockPantry();

  const [skipped, setSkipped] = useState<Set<number>>(new Set());
  const [amounts, setAmounts] = useState<Record<number, number>>({});

  if (isLoading) return <p className="center-note">Checking the basket…</p>;
  if (!list) {
    return (
      <section>
        <h1>Before you send</h1>
        <p className="muted">Accept a week's plan first.</p>
      </section>
    );
  }

  const rows = list.departments
    .flatMap((dept) => dept.items)
    .filter((item) => item.product_id && !skipped.has(item.ingredient_id));

  const quantityOf = (item: ListItem) =>
    amounts[item.ingredient_id] ?? item.buy_quantity ?? 1;

  const total = rows.reduce(
    (sum, item) => sum + Number(item.price ?? 0) * quantityOf(item),
    0,
  );

  function setQuantity(item: ListItem, next: number) {
    setAmounts((current) => ({
      ...current,
      [item.ingredient_id]: Math.max(1, next),
    }));
  }

  function skip(item: ListItem) {
    setSkipped((current) => new Set(current).add(item.ingredient_id));
  }

  function alreadyHave(item: ListItem) {
    // Into the pantry, not just off this list — so next week knows as well.
    stock.mutate({ ingredient_id: item.ingredient_id, note: 'already had it' });
    skip(item);
  }

  function send() {
    addToCart.mutate({
      storeId: list!.store_id,
      items: rows.map((item) => ({
        product_id: item.product_id,
        quantity: quantityOf(item),
      })),
    });
  }

  const failed = addToCart.error as (Error & { payload?: { needs_link?: boolean } }) | null;
  const needsLink = Boolean(failed?.payload?.needs_link);

  return (
    <section>
      <Link to="/list" className="back-link">
        ← Shopping list
      </Link>
      <h1>Before you send</h1>
      <p className="muted">
        These go into your {list.store} cart. Nothing is ordered and nothing is
        paid for — you check out at {list.store} yourself.
      </p>

      <ul className="cart-rows">
        {rows.map((item) => (
          <li key={item.ingredient_id} className="cart-row">
            <div className="cart-row__main">
              <span className="cart-row__product">{item.product_name || item.name}</span>
              <span className="muted small">
                {item.product_size && `${item.product_size} · `}
                for {item.name}
                {item.substituted_from && ` (instead of ${item.substituted_from})`}
              </span>
              {item.buy_is_estimate && (
                <span className="tag tag--warn">amount estimated</span>
              )}
            </div>

            <div className="cart-row__qty">
              <button
                type="button"
                className="btn btn--small"
                aria-label={`One fewer ${item.name}`}
                onClick={() => setQuantity(item, quantityOf(item) - 1)}
              >
                −
              </button>
              <span aria-live="polite">{quantityOf(item)}</span>
              <button
                type="button"
                className="btn btn--small"
                aria-label={`One more ${item.name}`}
                onClick={() => setQuantity(item, quantityOf(item) + 1)}
              >
                +
              </button>
            </div>

            <span className="price">
              {item.price && `$${(Number(item.price) * quantityOf(item)).toFixed(2)}`}
            </span>

            <div className="cart-row__actions">
              <button
                type="button"
                className="link-button"
                onClick={() => alreadyHave(item)}
              >
                already have it
              </button>
              <button
                type="button"
                className="link-button danger"
                title="Never match this product to this ingredient again"
                onClick={() => {
                  const match = item.match_id;
                  if (match) correctMatch.mutate({ id: match, accepted: false });
                  skip(item);
                }}
              >
                not this
              </button>
            </div>
          </li>
        ))}
      </ul>

      {rows.length === 0 && (
        <p className="muted">Nothing left to send — everything is ticked off or in the pantry.</p>
      )}

      <div className="plan-actions">
        <p className="list-total">
          <span className="list-total__amount">${total.toFixed(2)}</span>
          <span className="muted small"> · {rows.length} items</span>
        </p>

        {needsLink ? (
          <Link className="btn btn--primary btn--wide" to="/profile">
            Connect {list.store} first →
          </Link>
        ) : (
          /* Gone once it has been sent. The store's endpoint is cart/*add* --
             it stacks onto whatever is in the basket rather than replacing it,
             so pressing this twice orders the week twice, and the second press
             looks exactly like the first. Sending again is still possible
             below, but it has to be asked for in those words. */
          !addToCart.isSuccess && (
            <button
              className="btn btn--primary btn--wide"
              onClick={send}
              disabled={addToCart.isPending || rows.length === 0}
            >
              {addToCart.isPending ? (
                <>
                  <span className="spinner" aria-hidden="true" />
                  Sending…
                </>
              ) : (
                `Add ${rows.length} items to ${list.store} cart`
              )}
            </button>
          )
        )}

        {addToCart.isError && <p className="error">{failed?.message}</p>}

        {addToCart.data && (
          <>
            {/* Not "added": cart/add answers 200 having quietly dropped
                anything it could not resolve, so claiming success would
                sometimes be a lie. */}
            <p className="muted">
              Sent to your cart — check it over before you check out.
            </p>
            {addToCart.data.checkout_url && (
              <a
                className="btn btn--primary btn--wide"
                href={addToCart.data.checkout_url}
                target="_blank"
                rel="noreferrer"
              >
                Open your {list.store} cart →
              </a>
            )}
            <button className="btn btn--wide" onClick={send} disabled={addToCart.isPending}>
              Send these {rows.length} items again
            </button>
          </>
        )}
      </div>
    </section>
  );
}
